/**
 * ServiceOrderShipmentCard - Seção "Transportadora" do menu lateral da O.S.
 *
 * Mostra a remessa mais recente vinculada à O.S. reaproveitando a mesma
 * consulta da aba Remessas (`useShipmentsList`, cache compartilhado) e abre
 * o `ShipmentDetailsModal` oficial pelo olho. Oculta sem permissão
 * `log_shipments` ou sem remessas.
 *
 * CHANGELOG:
 *   RELEASE: 2026-10-16 v1.0.0 - Versão inicial.
 */
import { useState } from 'react';
import { format } from 'date-fns';
import { CalendarDays, Hash, Package, Tag, Truck, User } from 'lucide-react';
import { usePermissionsContext } from '@/contexts/PermissionsContext';
import { useShipmentsList } from '@/hooks/useShipments';
import { DetailsInfoRow, DetailsRelationshipRow, DetailsSidebarCard } from '@/components/shared/details-modal/DetailsModalPrimitives';
import { ShipmentTrackingCode } from '@/components/logistics/ShipmentTrackingCode';
import { ShipmentStatusBadge } from '@/components/logistics/ShipmentStatusBadge';
import { ShipmentDetailsModal } from '@/components/logistics/ShipmentDetailsModal';
import { recipientKindLabel } from '@/lib/shipmentLinkPermissions';

/** Data DATE (yyyy-MM-dd) sem deslocamento de fuso. */
const fmtDate = (d: string | null) => (d ? format(new Date(d.length === 10 ? `${d}T12:00:00` : d), 'dd/MM/yyyy') : null);

export function ServiceOrderShipmentCard({ serviceOrderId }: { serviceOrderId: string }) {
  const { canView, isAdmin } = usePermissionsContext();
  const allowView = isAdmin || canView('log_shipments');
  const [openId, setOpenId] = useState<string | null>(null);
  // Mesmos filtros da aba Remessas => mesma chave de cache (sem consulta extra)
  const list = useShipmentsList({
    search: '', status: null, carrierId: null, overdueOnly: false,
    entityType: 'service_order', entityId: serviceOrderId, page: 1, pageSize: 50,
  }, allowView);

  const rows = list.data?.items ?? [];
  if (!allowView || rows.length === 0) return null;
  const r = rows[0]; // lista já vem da mais recente para a mais antiga
  const extra = (list.data?.total ?? rows.length) - 1;

  return (
    <>
      <DetailsSidebarCard title="Transportadora" icon={Truck}>
        <DetailsRelationshipRow icon={Package} label="N. Envio" value={r.number} onView={() => setOpenId(r.id)} />
        <DetailsInfoRow icon={Tag} label="Tipo destino">{recipientKindLabel(r.recipient_customer_id, r.recipient_roles)}</DetailsInfoRow>
        <DetailsInfoRow icon={User} label="Nome destino">{r.recipient_name}</DetailsInfoRow>
        <DetailsInfoRow icon={Hash} label="N. Rastreio">
          <ShipmentTrackingCode code={r.tracking_code} />
        </DetailsInfoRow>
        <DetailsInfoRow icon={Truck} label="Status"><ShipmentStatusBadge status={r.status} /></DetailsInfoRow>
        <DetailsInfoRow icon={CalendarDays} label="Data previsão">
          {r.expected_delivery_date ? <span className={r.is_overdue ? 'font-medium text-destructive' : undefined}>{fmtDate(r.expected_delivery_date)}</span> : null}
        </DetailsInfoRow>
        {extra > 0 && <p className="pt-1 text-right text-[11px] text-muted-foreground">+{extra} remessa(s) na aba Remessas</p>}
      </DetailsSidebarCard>
      <ShipmentDetailsModal shipmentId={openId} open={!!openId} onOpenChange={(o) => !o && setOpenId(null)} onOpenShipment={setOpenId} />
    </>
  );
}
